import { useMemo, useState } from "react";
import { BOOK_PRICE } from "../../../utils/constants";
import { payWithPaystack } from "../../../utils/paystack";
import { createOrder } from "../../../services/orders";

import QuantityStep from "./QuantityStep";
import CountryStep from "./CountryStep";
import FulfilmentStep from "./FulfilmentStep";
import CollectionStep from "./CollectionStep";
import DetailsStep from "./DetailsStep";
import SummaryStep from "./SummaryStep";
import SuccessModal from "./SuccessModal";

export default function OrderPanel({
  qty,
  setQty,
  country,
  setCountry,
  fulfilment,
  setFulfilment,
  collection,
  setCollection,
  deliveryLocation,
  setDeliveryLocation,
}) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    notes: "",
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const total = useMemo(() => qty * BOOK_PRICE, [qty]);

  const canShowSummary =
    country &&
    fulfilment &&
    form?.firstName &&
    form?.lastName &&
    form?.email &&
    form?.phone &&
    (fulfilment === "collection"
      ? collection
      : form?.address && form?.city);

  const handleCheckout = async () => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      const orderId = `DM-${Date.now()}`;

      const orderPayload = {
        orderId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        country,
        quantity: qty,
        fulfilment,
        collectionPoint: collection?.name || "",
        address: form.address || "",
        city: form.city || "",
        notes: form.notes || "",
        amount: qty * BOOK_PRICE,
        paymentStatus: "Pending",
      };

      await createOrder(orderPayload);

      payWithPaystack({
        email: form.email,
        amount: qty * BOOK_PRICE,
        reference: orderId,

        metadata: {
          name: `${form.firstName} ${form.lastName}`,
          phone: form.phone,
          country,
          fulfilment,
          quantity: qty,
          collectionPoint: collection?.name || null,
          address: form.address || null,
          city: form.city || null,
        },

        onSuccess: async (transaction) => {
          try {
            const reference =
              transaction.reference || transaction.trxref;

            console.log("PAYSTACK SUCCESS:", transaction);
            console.log("VERIFYING REFERENCE:", reference);

            const response = await fetch("/api/verify-payment", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                reference,
                orderId,
              }),
            });

            const result = await response.json();

            console.log("VERIFICATION RESULT:", result);

            if (!response.ok || !result.success) {
              throw new Error(
                result.message || "Payment verification failed"
              );
            }

            setShowSuccess(true);
          } catch (error) {
            console.error("Payment verification error:", error);

            alert(
              `Your payment was successful, but we could not confirm the order automatically: ${error.message}`
            );
          } finally {
            setIsProcessing(false);
          }
        },

        onClose: () => {
          setIsProcessing(false);
          console.log("Payment window closed");
        },
      });
    } catch (error) {
      setIsProcessing(false);

      console.error("Checkout error:", error);

      alert(
        "We couldn't start your order. Please try again or contact us."
      );
    }
  };

  return (
    <>
      <style>{`
        .order-panel {
          padding: 52px 56px;
          border-right: 1px solid rgba(26, 22, 17, 0.12);
        }

        .order-panel-title {
          font-family: "Cormorant Garamond", serif;
          font-size: 2.4rem;
          font-weight: 300;
          margin: 0 0 6px;
          line-height: 1.1;
        }

        .order-panel-subtitle {
          font-size: 1rem;
          text-transform: uppercase;
          color: #000;
          margin: 0 0 36px;
          letter-spacing: 0.04em;
        }

        @media (max-width: 768px) {
          .order-panel {
            padding: 32px 20px 40px;
            border-right: none;
          }

          .order-panel-title {
            font-size: 2rem;
            line-height: 1.05;
          }

          .order-panel-subtitle {
            font-size: 0.72rem;
            margin-bottom: 28px;
            line-height: 1.5;
          }
        }

        @media (max-width: 420px) {
          .order-panel {
            padding-left: 16px;
            padding-right: 16px;
          }

          .order-panel-title {
            font-size: 1.85rem;
          }
        }
      `}</style>

      <main className="order-panel">
        {/* TITLE */}
        <h1 className="order-panel-title">
          Order{" "}
          <em style={{ color: "#C4773B" }}>
            Decolonizing Mission
          </em>
        </h1>

        <p className="order-panel-subtitle">
          by Dr Harvey C. Kwiyani · Africa Shipping
        </p>

        {/* STEP FLOW */}
        <QuantityStep
          qty={qty}
          setQty={setQty}
        />

        <CountryStep
          country={country}
          setCountry={setCountry}
        />

        {country && (
          <FulfilmentStep
            country={country}
            fulfilment={fulfilment}
            setFulfilment={setFulfilment}
          />
        )}

        {fulfilment === "collection" && country && (
          <CollectionStep
            country={country}
            collection={collection}
            setCollection={setCollection}
          />
        )}

        {fulfilment && (
          <DetailsStep
            form={form}
            setForm={setForm}
            fulfilment={fulfilment}
          />
        )}

        {fulfilment && (
          <SummaryStep
            qty={qty}
            country={country}
            fulfilment={fulfilment}
            collection={collection}
            form={form}
            onCheckout={handleCheckout}
            isProcessing={isProcessing}
          />
        )}

        {showSuccess && (
          <SuccessModal
            onClose={() => {
              window.location.reload();
            }}
          />
        )}
      </main>
    </>
  );
}